import * as THREE from 'three';
import { AppState, Seasons } from '../core/constants.js';

const TREE_HEIGHT = 5.6;
const TREE_BASE_RADIUS = 2.35;

function randomFromPalette(palette) {
  return palette[Math.floor(Math.random() * palette.length)];
}

function makeCanvasTexture(drawer) {
  const canvas = document.createElement('canvas');
  canvas.width = 160;
  canvas.height = 160;
  const ctx = canvas.getContext('2d');
  drawer(ctx, canvas.width, canvas.height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function blossomTexture() {
  return makeCanvasTexture((ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.translate(w / 2, h / 2);
    for (let i = 0; i < 5; i += 1) {
      ctx.save();
      ctx.rotate((i / 5) * Math.PI * 2);
      const grad = ctx.createRadialGradient(0, -30, 6, 0, -36, 30);
      grad.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
      grad.addColorStop(1, 'rgba(255, 191, 222, 0.94)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.ellipse(0, -36, 20, 33, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    ctx.fillStyle = 'rgba(255, 226, 244, 0.95)';
    ctx.beginPath();
    ctx.arc(0, 0, 10, 0, Math.PI * 2);
    ctx.fill();
  });
}

function leafTexture(baseColor = '#4a9b5b', veinColor = '#225333') {
  return makeCanvasTexture((ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.translate(w / 2, h / 2 + 8);
    ctx.rotate(-0.32);

    ctx.fillStyle = baseColor;
    ctx.beginPath();
    ctx.moveTo(0, -50);
    ctx.quadraticCurveTo(30, -26, 36, 8);
    ctx.quadraticCurveTo(26, 42, 0, 58);
    ctx.quadraticCurveTo(-30, 40, -38, 4);
    ctx.quadraticCurveTo(-30, -29, 0, -50);
    ctx.fill();

    ctx.strokeStyle = veinColor;
    ctx.lineWidth = 3.6;
    ctx.beginPath();
    ctx.moveTo(0, -43);
    ctx.lineTo(0, 48);
    ctx.stroke();

    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.42;
    ctx.beginPath();
    ctx.moveTo(0, -12);
    ctx.lineTo(17, 5);
    ctx.moveTo(0, 8);
    ctx.lineTo(15, 24);
    ctx.moveTo(0, -8);
    ctx.lineTo(-16, 10);
    ctx.moveTo(0, 16);
    ctx.lineTo(-14, 31);
    ctx.stroke();
  });
}

function snowClumpTexture() {
  return makeCanvasTexture((ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.translate(w / 2, h / 2 + 4);
    const grad = ctx.createRadialGradient(0, 0, 6, 0, 0, 52);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(1, 'rgba(209,229,255,0.86)');
    ctx.fillStyle = grad;

    const blobs = [
      { x: -22, y: 6, r: 20 },
      { x: -8, y: -8, r: 24 },
      { x: 14, y: -4, r: 22 },
      { x: 28, y: 10, r: 18 },
      { x: 0, y: 16, r: 26 }
    ];

    blobs.forEach((blob) => {
      ctx.beginPath();
      ctx.arc(blob.x, blob.y, blob.r, 0, Math.PI * 2);
      ctx.fill();
    });
  });
}

function snowflakeTexture() {
  return makeCanvasTexture((ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.translate(w / 2, h / 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.lineWidth = 4;
    for (let i = 0; i < 6; i += 1) {
      ctx.save();
      ctx.rotate((i / 6) * Math.PI * 2);
      ctx.beginPath();
      ctx.moveTo(0, -34);
      ctx.lineTo(0, 34);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, -20);
      ctx.lineTo(8, -12);
      ctx.moveTo(0, -20);
      ctx.lineTo(-8, -12);
      ctx.moveTo(0, 20);
      ctx.lineTo(8, 12);
      ctx.moveTo(0, 20);
      ctx.lineTo(-8, 12);
      ctx.stroke();
      ctx.restore();
    }
  });
}

function buildClosedPosition(index, total) {
  const ratio = total === 1 ? 0.5 : index / (total - 1);
  const y = 0.7 + ratio * TREE_HEIGHT;
  const radius = (1 - Math.pow(ratio, 0.82)) * TREE_BASE_RADIUS;
  const angle = index * 2.399963229728653 + Math.sin(index * 0.41) * 0.3;
  const spread = radius * (0.68 + Math.random() * 0.46);
  return new THREE.Vector3(Math.cos(angle) * spread, y, Math.sin(angle) * spread);
}

function buildDispersedPosition(index) {
  const radius = 3.1 + Math.random() * 6.8;
  const theta = index * 0.71 + Math.random() * 0.7;
  const y = 1.2 + Math.random() * 6.5;
  return new THREE.Vector3(Math.cos(theta) * radius, y, Math.sin(theta) * radius);
}

export class TreeSystem {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();

    this.trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.24, 0.49, 2.65, 18),
      new THREE.MeshStandardMaterial({ color: 0x5d3f2e, roughness: 0.82, metalness: 0.04 })
    );
    this.trunk.position.set(0, 1.2, 0);

    this.decorGroup = new THREE.Group();
    this.snowfallGroup = new THREE.Group();

    this.group.add(this.trunk);
    this.group.add(this.decorGroup);
    this.group.add(this.snowfallGroup);
    this.scene.add(this.group);

    this.textureCache = {
      blossom: blossomTexture(),
      leaf: leafTexture(),
      autumnLeaf: leafTexture('#df8b34', '#7b421b'),
      snowClump: snowClumpTexture(),
      snowflake: snowflakeTexture()
    };

    this.items = [];
    this.snowflakes = [];
    this.currentSeason = Seasons.SPRING;
  }

  clearSprites() {
    this.items.forEach((item) => {
      this.decorGroup.remove(item.sprite);
      item.sprite.material.dispose();
    });
    this.items = [];
  }

  clearSnowfall() {
    this.snowflakes.forEach((flake) => {
      this.snowfallGroup.remove(flake.sprite);
      flake.sprite.material.dispose();
    });
    this.snowflakes = [];
  }

  applySeason(profile) {
    this.currentSeason = profile.id;
    this.trunk.material.color.setHex(profile.trunkColor);
    this.clearSprites();
    this.clearSnowfall();

    const count = profile.elementCount ?? profile.particleCount ?? 900;
    const tex = this.textureCache[profile.elementType] ?? this.textureCache.leaf;

    for (let i = 0; i < count; i += 1) {
      const color = randomFromPalette(profile.colors);
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: tex,
          color,
          transparent: true,
          opacity: 0.94,
          depthWrite: false
        })
      );

      const scale = (profile.scaleMin ?? 0.15) + Math.random() * ((profile.scaleMax ?? 0.28) - (profile.scaleMin ?? 0.15));
      sprite.scale.set(scale, scale, 1);

      const closedPos = buildClosedPosition(i, count);
      const dispersedPos = buildDispersedPosition(i);
      sprite.position.copy(closedPos);
      this.decorGroup.add(sprite);

      this.items.push({
        sprite,
        closedPos,
        dispersedPos,
        baseScale: scale,
        seed: {
          speed: 0.45 + Math.random() * 1.25,
          phase: Math.random() * Math.PI * 2,
          amp: 0.05 + Math.random() * 0.24
        }
      });
    }

    if (profile.id === Seasons.WINTER) {
      const snowCount = profile.snowfallCount ?? 220;
      for (let i = 0; i < snowCount; i += 1) {
        const sprite = new THREE.Sprite(
          new THREE.SpriteMaterial({
            map: this.textureCache.snowflake,
            color: 0xffffff,
            transparent: true,
            opacity: 0.9,
            depthWrite: false
          })
        );
        const scale = 0.08 + Math.random() * 0.14;
        sprite.scale.set(scale, scale, 1);
        sprite.position.set((Math.random() - 0.5) * 18, Math.random() * 13 + 1, (Math.random() - 0.5) * 18);
        this.snowfallGroup.add(sprite);

        this.snowflakes.push({
          sprite,
          velocity: 0.6 + Math.random() * 1.6,
          swing: 0.4 + Math.random() * 1.2,
          phase: Math.random() * Math.PI * 2
        });
      }
    }
  }

  update(state, profile, elapsedSeconds, deltaSeconds = 1 / 60) {
    const isClosed = state === AppState.CLOSED;
    const isAutumn = profile.id === Seasons.AUTUMN;
    const inWinter = profile.id === Seasons.WINTER;

    this.items.forEach((item) => {
      const target = isClosed ? item.closedPos : item.dispersedPos;
      let tx = target.x;
      let ty = target.y;
      let tz = target.z;

      if (!isClosed) {
        const wind = profile.driftStrength * item.seed.amp;
        tx += Math.sin(elapsedSeconds * item.seed.speed + item.seed.phase) * wind;
        tz += Math.cos(elapsedSeconds * item.seed.speed * 0.76 + item.seed.phase) * wind;
        ty += Math.sin(elapsedSeconds * item.seed.speed * 0.58 + item.seed.phase) * wind * 0.45;

        if (isAutumn) {
          ty -= ((elapsedSeconds * 0.42 + item.seed.phase) % 2.6) * 0.18;
          item.sprite.material.rotation += deltaSeconds * 0.75;
        } else {
          item.sprite.material.rotation *= 0.94;
        }
      } else {
        item.sprite.material.rotation *= 0.86;
      }

      item.sprite.position.x += (tx - item.sprite.position.x) * (isClosed ? 0.15 : 0.11);
      item.sprite.position.y += (ty - item.sprite.position.y) * (isClosed ? 0.15 : 0.11);
      item.sprite.position.z += (tz - item.sprite.position.z) * (isClosed ? 0.15 : 0.11);

      const targetScale = isClosed ? item.baseScale : item.baseScale * (isAutumn ? 1.08 : 0.95);
      const nextScale = item.sprite.scale.x + (targetScale - item.sprite.scale.x) * 0.15;
      item.sprite.scale.set(nextScale, nextScale, 1);
      item.sprite.material.opacity += ((isClosed ? 0.95 : 0.88) - item.sprite.material.opacity) * 0.12;
    });

    this.snowfallGroup.visible = inWinter;
    if (!inWinter) {
      return;
    }

    this.snowflakes.forEach((flake) => {
      const p = flake.sprite.position;
      p.y -= flake.velocity * deltaSeconds;
      p.x += Math.sin(elapsedSeconds * flake.swing + flake.phase) * deltaSeconds * 0.9;
      p.z += Math.cos(elapsedSeconds * (flake.swing * 0.8) + flake.phase) * deltaSeconds * 0.5;
      flake.sprite.material.rotation += deltaSeconds * 0.22;

      if (p.y < 0.4) {
        p.y = 12 + Math.random() * 4;
        p.x = (Math.random() - 0.5) * 18;
        p.z = (Math.random() - 0.5) * 18;
      }
    });
  }

  dispose() {
    this.clearSprites();
    this.clearSnowfall();
    this.trunk.geometry.dispose();
    this.trunk.material.dispose();
    Object.values(this.textureCache).forEach((tex) => tex.dispose());
    this.scene.remove(this.group);
  }
}
